import React, { useState } from "react";
import { IconButton, Message, useToaster } from "rsuite";
import TrashIcon from "@rsuite/icons/Trash";
import axiosInstance from "../../../../../../appHelpers/axiosInstance";
import { useAuth0 } from "@auth0/auth0-react";
import Modal from "../../../../../ui/Modal/Modal";
import Button from "../../../../../ui/Button/Button";
import styled from "styled-components";
import { colors } from "../../../../../ui/theme/colors";

export const DeleteSiteGroup = ({ group, update, setUpdate }) => {
  const { getAccessTokenSilently } = useAuth0();
  const toaster = useToaster();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const successMessage = (
    <Message showIcon type="success" header="Success">
      Site group "{group.name}" was deleted successfully.
    </Message>
  );

  const errorMessage = (
    <Message showIcon type="error" header="Error">
      Failed to delete site group. Please try again.
    </Message>
  );

  const onDelete = async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "DELETE",
        url: `/customer/site-groups/${group.id}`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      });

      setUpdate(!update);
      toaster.push(successMessage, "topCenter");
      handleClose();
    } catch (error) {
      console.error("Error deleting site group:", error);
      toaster.push(errorMessage, "topCenter");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <IconButton
        icon={<TrashIcon />}
        onClick={handleOpen}
        size="sm"
        appearance="subtle"
        color="red"
      />

      <Modal
        open={open}
        onClose={handleClose}
        title="Delete Site Group"
        size="sm"
        showFooter={true}
        footerActions={
          <StyledFooterActions>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={onDelete} loading={loading}>
              Delete Group
            </Button>
          </StyledFooterActions>
        }
      >
        <ModalContent>
          <ConfirmationContainer>
            <IconContainer>
              <TrashIcon />
            </IconContainer>

            <Title>Are you sure?</Title>

            <Description>
              You are about to delete the site group{" "}
              <GroupName>"{group.name}"</GroupName>.
            </Description>

            <WarningMessage>
              <WarningIcon>⚠️</WarningIcon>
              <WarningText>
                <strong>Warning:</strong> Any sites assigned to this group will
                become unassigned. This action cannot be undone.
              </WarningText>
            </WarningMessage>
          </ConfirmationContainer>
        </ModalContent>
      </Modal>
    </>
  );
};

const ModalContent = styled.div`
  padding: 24px;
`;

const StyledFooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const ConfirmationContainer = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const IconContainer = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${colors.error.light};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.error.main};
  font-size: 36px;
  border: 2px solid ${colors.error.border};
`;

const Title = styled.h3`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: ${colors.neutral[800]};
`;

const Description = styled.p`
  margin: 0;
  font-size: 16px;
  color: ${colors.neutral[600]};
  line-height: 1.5;
  text-align: center;
`;

const GroupName = styled.span`
  font-weight: 600;
  color: ${colors.neutral[800]};
`;

const WarningMessage = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: ${colors.error.light};
  border: 1px solid ${colors.error.border};
  border-radius: 8px;
  padding: 16px;
  text-align: left;
  max-width: 100%;
`;

const WarningIcon = styled.div`
  font-size: 20px;
  flex-shrink: 0;
  margin-top: 2px;
`;

const WarningText = styled.div`
  font-size: 14px;
  color: ${colors.error.main};
  line-height: 1.4;

  strong {
    font-weight: 600;
  }
`;
